import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, ArrowRight, Lightbulb, X, ThumbsUp, ThumbsDown, BookOpen, ChevronRight,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { helpAPI } from '../lib/api';
import { toast } from 'sonner';
import { GUIDES, GROUP_ORDER, shotPrimary, shotFallback } from '../data/helpGuides';

// ---- Framed browser-style screenshot card (docs look) ----
// Prefers the backend-served (one-click-refreshable) asset, falls back to the
// bundled copy that always ships with the frontend build.
function Screenshot({ shot, caption }) {
  const [src, setSrc] = useState(shotPrimary(shot));
  useEffect(() => { setSrc(shotPrimary(shot)); }, [shot]);
  return (
    <figure className="mt-5 overflow-hidden rounded-xl border border-border bg-muted/30 shadow-lg shadow-black/10" data-testid="help-screenshot">
      <div className="flex items-center gap-1.5 border-b border-border bg-muted/60 px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
      </div>
      <img
        src={src}
        alt={caption || 'Product screenshot'}
        loading="lazy"
        className="w-full"
        onError={() => { if (src !== shotFallback(shot)) setSrc(shotFallback(shot)); }}
      />
      {caption && (
        <figcaption className="border-t border-border px-4 py-2 text-xs text-muted-foreground">{caption}</figcaption>
      )}
    </figure>
  );
}

export default function HelpHub() {
  const [query, setQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeId, setActiveId] = useState(GUIDES[0].id);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const contentRef = useRef(null);

  const active = useMemo(() => GUIDES.find((g) => g.id === activeId) || GUIDES[0], [activeId]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const selectGuide = useCallback((id, { push = true, step = null } = {}) => {
    setActiveId(id);
    setShowComment(false);
    setComment('');
    helpAPI.track(id).catch(() => {});
    if (step != null) {
      setTimeout(() => scrollTo(`step-${id}-${step}`), 120);
    } else if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (push) {
      const sp = new URLSearchParams(searchParams);
      sp.set('guide', id);
      if (step != null) sp.set('step', String(step)); else sp.delete('step');
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep-link + ⌘K navigation: react to ?guide=<id>&step=<i> changes.
  useEffect(() => {
    const gid = searchParams.get('guide');
    const stepParam = searchParams.get('step');
    const step = stepParam != null && !Number.isNaN(Number(stepParam)) ? Number(stepParam) : null;
    if (gid && GUIDES.some((g) => g.id === gid)) {
      if (gid !== activeId) {
        selectGuide(gid, { push: false, step });
      } else if (step != null) {
        setTimeout(() => scrollTo(`step-${gid}-${step}`), 120);
      }
    }
  }, [searchParams]);

  // Track the default guide open when no deep-link is present.
  useEffect(() => {
    if (!searchParams.get('guide')) helpAPI.track(GUIDES[0].id).catch(() => {});
  }, []);

  const sendFeedback = async (helpful) => {
    setFeedbackGiven((m) => ({ ...m, [active.id]: helpful ? 'up' : 'down' }));
    try {
      await helpAPI.feedback(active.id, helpful, helpful ? undefined : comment.trim() || undefined);
      toast.success('Thanks for the feedback!');
    } catch (_e) {
      toast.error('Could not save feedback');
    }
    setShowComment(false);
    setComment('');
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GUIDES;
    return GUIDES.filter((g) =>
      [g.title, g.summary, g.best, ...(g.tags || [])].join(' ').toLowerCase().includes(q)
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((g) => { (map[g.group] = map[g.group] || []).push(g); });
    return GROUP_ORDER.filter((k) => map[k]).map((k) => ({ group: k, items: map[k] }));
  }, [filtered]);

  const ActiveIcon = active.icon;

  return (
    <div className="mx-auto max-w-[1400px]" data-testid="help-hub-page">
      {/* Header */}
      <div className="border-b border-border pb-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5 text-primary" /> Documentation
        </div>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Help &amp; Best Practices</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Step-by-step guides with screenshots from the actual product — from importing leads to running
          campaigns and revealing verified contacts.
        </p>
        <div className="relative mt-5 max-w-xl">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documentation — e.g. import, campaign, reveal…"
            className="h-11 rounded-xl pl-10"
            data-testid="help-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="help-search-clear"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile guide picker */}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2 lg:hidden" data-testid="help-mobile-nav">
        {filtered.map((g) => (
          <button
            key={g.id}
            onClick={() => selectGuide(g.id)}
            className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              g.id === activeId ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
            }`}
          >
            {g.title}
          </button>
        ))}
      </div>

      <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)_200px]">
        {/* Left docs nav */}
        <aside className="hidden lg:block" data-testid="help-nav">
          <nav className="sticky top-6 space-y-6">
            {grouped.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="help-no-results">No guides match “{query}”.</p>
            ) : grouped.map(({ group, items }) => (
              <div key={group}>
                <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{group}</p>
                <ul className="space-y-0.5">
                  {items.map((g) => {
                    const Icon = g.icon;
                    const isActive = g.id === activeId;
                    return (
                      <li key={g.id}>
                        <button
                          onClick={() => selectGuide(g.id)}
                          data-testid={`help-nav-${g.id}`}
                          className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                            isActive ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate">{g.title}</span>
                          {g.plan && <span className="text-[10px] font-semibold text-amber-500">{g.plan}+</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Center content */}
        <article ref={contentRef} className="min-w-0 pt-2" data-testid="help-content">
          <div className="inline-flex items-center gap-2 rounded-md bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
            <ActiveIcon className="h-3.5 w-3.5" /> {active.group}
          </div>
          <h2 className="mt-3 flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground" data-testid="help-active-title">
            {active.title}
            {active.plan && (
              <Badge variant="outline" className="border-amber-500/30 bg-amber-500/15 text-[10px] text-amber-500">{active.plan}+</Badge>
            )}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">{active.summary}</p>

          {/* Reference table (VoiceLink-style) */}
          {active.reference && (
            <section id="reference" className="mt-6 scroll-mt-6">
              <h3 className="text-sm font-semibold text-foreground">Key concepts</h3>
              <div className="mt-2 overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm" data-testid="help-reference-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="w-40 px-4 py-2 font-semibold">Item</th>
                      <th className="px-4 py-2 font-semibold">What it means</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.reference.map(([term, desc]) => (
                      <tr key={term} className="border-b border-border last:border-0">
                        <td className="px-4 py-2.5 font-medium text-foreground">{term}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Steps */}
          <section id="steps" className="mt-8 scroll-mt-6">
            <h3 className="text-sm font-semibold text-foreground">Step by step</h3>
            <ol className="mt-3 space-y-5" data-testid="help-detail-steps">
              {active.steps.map((s, i) => (
                <li key={i} id={`step-${active.id}-${i}`} className="flex gap-3 scroll-mt-6">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">{s.title}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* Real product screenshot */}
          <section id="screenshot" className="mt-8 scroll-mt-6">
            <h3 className="text-sm font-semibold text-foreground">What it looks like</h3>
            <Screenshot shot={active.shot} caption={active.shotCaption} />
          </section>

          {/* Best practice */}
          <div id="best-practice" className="mt-8 flex scroll-mt-6 gap-3 rounded-lg border-l-2 border-primary bg-primary/10 px-4 py-3">
            <Lightbulb className="h-4 w-4 shrink-0 text-primary" />
            <p className="text-sm text-foreground"><span className="font-semibold">Best practice: </span>{active.best}</p>
          </div>

          {active.cta && (
            <Button asChild className="mt-6" data-testid="help-detail-cta">
              <Link to={active.cta.href}>{active.cta.label} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          )}

          {/* Feedback */}
          <div className="mt-8 border-t border-border pt-5" data-testid="help-feedback">
            {feedbackGiven[active.id] ? (
              <span className="text-sm font-medium text-primary" data-testid="help-feedback-thanks">Thanks for the feedback! 🙌</span>
            ) : showComment ? (
              <div className="max-w-md space-y-2" data-testid="help-feedback-comment">
                <label className="text-sm font-medium text-foreground">What was missing?</label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={280}
                  rows={2}
                  placeholder="Optional — one line helps us fix the doc"
                  data-testid="help-feedback-comment-input"
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => sendFeedback(false)} data-testid="help-feedback-comment-submit">Send feedback</Button>
                  <Button size="sm" variant="ghost" onClick={() => sendFeedback(false)} data-testid="help-feedback-comment-skip">Skip</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Was this helpful?</span>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => sendFeedback(true)} data-testid="help-feedback-up">
                  <ThumbsUp className="h-3.5 w-3.5" /> Yes
                </Button>
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setShowComment(true)} data-testid="help-feedback-down">
                  <ThumbsDown className="h-3.5 w-3.5" /> No
                </Button>
              </div>
            )}
          </div>
        </article>

        {/* Right "On this page" TOC */}
        <aside className="hidden lg:block" data-testid="help-toc">
          <div className="sticky top-6">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">On this page</p>
            <ul className="space-y-1.5 border-l border-border">
              {active.reference && (
                <li><button onClick={() => scrollTo('reference')} className="flex w-full items-center gap-1 border-l-2 border-transparent px-3 text-left text-xs text-muted-foreground hover:border-primary hover:text-foreground">Key concepts</button></li>
              )}
              {active.steps.map((s, i) => (
                <li key={i}>
                  <button onClick={() => scrollTo(`step-${active.id}-${i}`)} className="flex w-full items-center gap-1 border-l-2 border-transparent px-3 text-left text-xs text-muted-foreground hover:border-primary hover:text-foreground">
                    <ChevronRight className="h-3 w-3 shrink-0" /> {s.title}
                  </button>
                </li>
              ))}
              <li><button onClick={() => scrollTo('screenshot')} className="flex w-full items-center gap-1 border-l-2 border-transparent px-3 text-left text-xs text-muted-foreground hover:border-primary hover:text-foreground">What it looks like</button></li>
              <li><button onClick={() => scrollTo('best-practice')} className="flex w-full items-center gap-1 border-l-2 border-transparent px-3 text-left text-xs text-muted-foreground hover:border-primary hover:text-foreground">Best practice</button></li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
